import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { PlusIcon, EditIcon, TrashIcon, FolderIcon, TagIcon } from 'lucide-react';
import { toast } from 'sonner';
import ExpenseCategoryFormModal from './ExpenseCategoryFormModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExpenseCategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/harcama-kategorileri`);
      const categoriesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Kategoriler yüklenirken hata:', error);
      toast.error('Kategoriler yüklenirken hata oluştu');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`"${category.name}" kategorisini silmek istediğinizden emin misiniz? Bu kategoriye bağlı tüm alt kategoriler de silinecektir.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/harcama-kategorileri/${category.id}`);
      toast.success('Kategori silindi');
      fetchCategories();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Kategori silinirken hata oluştu';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Kategori silinirken hata oluştu');
    }
  };

  const openModalForNew = () => {
    setEditingCategory(null);
    setModalOpen(true);
  };

  const openModalForEdit = (category) => {
    setEditingCategory(category);
    setModalOpen(true);
  };

  const filteredCategories = categories.filter((cat) => {
    const searchLower = searchTerm.toLowerCase();
    const nameMatch = cat.name?.toLowerCase().includes(searchLower);
    const subcategoryMatch = cat.subcategories?.some((sub) =>
      sub.name?.toLowerCase().includes(searchLower)
    );
    return nameMatch || subcategoryMatch;
  });

  return (
    <div className="p-6">
      <Card className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TagIcon className="h-6 w-6 text-teal-600" />
              <h2 className="text-2xl font-bold text-gray-900">Harcama Kategorileri</h2>
            </div>
            <Button
              onClick={openModalForNew}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="expense-cat-new-btn"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Yeni Kategori Ekle
            </Button>
          </div>

          <div className="mb-4">
            <Input
              placeholder="Kategori veya alt kategori ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Yükleniyor...</p>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <TagIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {searchTerm ? 'Arama sonucu bulunamadı' : 'Henüz harcama kategorisi eklenmemiş'}
            </p>
            <p className="text-gray-400 mt-2">
              {searchTerm ? 'Farklı bir arama terimi deneyin' : 'Başlamak için "Yeni Kategori Ekle" butonuna tıklayın'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredCategories.map((category) => (
              <Card key={category.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <FolderIcon className="h-5 w-5 text-teal-600" />
                      <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                      {/* Kaynak rozeti — global_id doluysa sistem senkronu,
                          boşsa kullanıcının kendi eklediği kategori. */}
                      {category.global_id ? (
                        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px] px-2 py-0.5" data-testid={`cat-src-${category.id}`}>
                          Sistem
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] px-2 py-0.5" data-testid={`cat-src-${category.id}`}>
                          Kullanıcı
                        </Badge>
                      )}
                    </div>

                    {category.subcategories && category.subcategories.length > 0 && (
                      <div className="ml-7">
                        <p className="text-xs text-gray-500 mb-2">Alt Kategoriler:</p>
                        <div className="flex flex-wrap gap-2">
                          {category.subcategories.map((sub) => (
                            <Badge
                              key={sub.id || sub.name}
                              variant="outline"
                              className="px-3 py-1 text-xs bg-gray-50 text-gray-700 border-gray-300"
                            >
                              {sub.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openModalForEdit(category)}
                      className="text-teal-600 hover:text-teal-700 hover:border-teal-600"
                    >
                      <EditIcon className="h-4 w-4 mr-1" />
                      Düzenle
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteCategory(category)}
                      className="text-red-600 hover:text-red-700 hover:border-red-600"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Sil
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      <ExpenseCategoryFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        editingCategory={editingCategory}
        onSaved={fetchCategories}
      />
    </div>
  );
};

export default ExpenseCategoryManagement;
